import { useState, useCallback, useEffect, useMemo } from "react";
import WheelPicker from "@/components/base/WheelPicker";
import { solarToLunar, LUNAR_MONTH_NAMES, LUNAR_DAY_NAMES, SHICHEN_LIST } from "@/utils/lunar";

type CalType = "solar" | "lunar";

interface BirthdayPickerProps {
  onConfirm: (value: string, displayText: string) => void;
  onCancel: () => void;
}

// ── 公历辅助 ──────────────────────────────────────────────
function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

// ── 年份范围 ──────────────────────────────────────────────
const SOLAR_YEARS = Array.from({ length: 101 }, (_, i) => `${1930 + i}`);
const SOLAR_MONTHS = Array.from({ length: 12 }, (_, i) => `${i + 1}月`);
const SHICHEN_LABELS = SHICHEN_LIST.map((s) => s.label);

// ── 农历年份 ──────────────────────────────────────────────
const GAN = ["甲","乙","丙","丁","戊","己","庚","辛","壬","癸"];
const ZHI = ["子","丑","寅","卯","辰","巳","午","未","申","酉","戌","亥"];
const LUNAR_YEARS = Array.from({ length: 101 }, (_, i) => {
  const y = 1930 + i;
  return GAN[(y - 4) % 10] + ZHI[(y - 4) % 12] + `(${y})`;
});
const LUNAR_MONTHS = LUNAR_MONTH_NAMES.map((m) => m + "月");
const LUNAR_DAYS = LUNAR_DAY_NAMES;

export default function BirthdayPicker({ onConfirm, onCancel }: BirthdayPickerProps) {
  const [calType, setCalType] = useState<CalType>("solar");

  // 公历状态
  const [sYearIdx, setSYearIdx] = useState(66); // 1996
  const [sMonthIdx, setSMonthIdx] = useState(0);
  const [sDayIdx, setSDayIdx] = useState(0);
  const [sShichenIdx, setSShichenIdx] = useState(0);

  // 农历状态
  const [lYearIdx, setLYearIdx] = useState(66);
  const [lMonthIdx, setLMonthIdx] = useState(0);
  const [lDayIdx, setLDayIdx] = useState(0);
  const [lShichenIdx, setLShichenIdx] = useState(0);

  // 公历天数列表（随年月变化）
  const solarDays = useMemo(() => {
    const year = 1930 + sYearIdx;
    const month = sMonthIdx + 1;
    const count = getDaysInMonth(year, month);
    return Array.from({ length: count }, (_, i) => `${i + 1}日`);
  }, [sYearIdx, sMonthIdx]);

  // 农历天数列表（固定30天，实际农历月天数不做严格限制）
  const lunarDays = useMemo(() => LUNAR_DAYS.slice(0, 30), []);

  // 当月天数变化时，修正 dayIdx 越界
  useEffect(() => {
    if (sDayIdx >= solarDays.length) setSDayIdx(solarDays.length - 1);
  }, [solarDays, sDayIdx]);

  // 计算显示文本
  const displayText = useMemo(() => {
    if (calType === "solar") {
      const year = 1930 + sYearIdx;
      const month = sMonthIdx + 1;
      const day = sDayIdx + 1;
      const shichen = SHICHEN_LIST[sShichenIdx].value;
      // 转农历显示
      try {
        const lunar = solarToLunar(year, month, day);
        return `农历:${lunar.year}年${lunar.monthStr}${lunar.dayStr} ${shichen}`;
      } catch {
        return `${year}年${month}月${day}日 ${shichen}`;
      }
    } else {
      const year = 1930 + lYearIdx;
      const month = lMonthIdx + 1;
      const day = lDayIdx + 1;
      const shichen = SHICHEN_LIST[lShichenIdx].value;
      return `农历:${year}年${LUNAR_MONTH_NAMES[lMonthIdx]}月${LUNAR_DAY_NAMES[lDayIdx]} ${shichen}`;
    }
  }, [calType, sYearIdx, sMonthIdx, sDayIdx, sShichenIdx, lYearIdx, lMonthIdx, lDayIdx, lShichenIdx]);

  const handleConfirm = useCallback(() => {
    let value = "";
    if (calType === "solar") {
      const year = 1930 + sYearIdx;
      const month = sMonthIdx + 1;
      const day = sDayIdx + 1;
      const shichen = SHICHEN_LIST[sShichenIdx].value;
      value = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")} ${shichen}`;
    } else {
      const year = 1930 + lYearIdx;
      const month = lMonthIdx + 1;
      const day = lDayIdx + 1;
      const shichen = SHICHEN_LIST[lShichenIdx].value;
      value = `农历${year}年${LUNAR_MONTH_NAMES[lMonthIdx]}月${LUNAR_DAY_NAMES[lDayIdx]} ${shichen}`;
    }
    onConfirm(value, displayText);
  }, [calType, sYearIdx, sMonthIdx, sDayIdx, sShichenIdx, lYearIdx, lMonthIdx, lDayIdx, lShichenIdx, displayText, onConfirm]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-t-2xl overflow-hidden"
        style={{ background: "#fff9f0", fontFamily: "'Noto Serif SC', serif" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部显示当前选择 */}
        <div
          className="px-4 py-3 text-center text-sm font-semibold"
          style={{ background: "#fff9f0", borderBottom: "1px solid #e8c87a", color: "#8b1a1a" }}
        >
          {displayText}
        </div>

        {/* 公历/农历切换 */}
        <div className="flex mx-4 my-3 rounded-lg overflow-hidden" style={{ border: "1.5px solid #c0000a" }}>
          {(["solar", "lunar"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setCalType(t)}
              className="flex-1 py-2 text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              style={{
                background: calType === t ? "#c0000a" : "transparent",
                color: calType === t ? "#fff" : "#c0000a",
                border: "none",
                fontFamily: "'Noto Serif SC', serif",
              }}
            >
              {t === "solar" ? "公历" : "农历"}
            </button>
          ))}
        </div>

        {/* 滚轮区域 */}
        <div className="flex px-2 pb-2" style={{ borderBottom: "1px solid #e8c87a" }}>
          {calType === "solar" ? (
            <>
              <div className="flex-1">
                <WheelPicker
                  items={SOLAR_YEARS}
                  selectedIndex={sYearIdx}
                  onChange={setSYearIdx}
                />
              </div>
              <div className="flex-none w-16">
                <WheelPicker
                  items={SOLAR_MONTHS}
                  selectedIndex={sMonthIdx}
                  onChange={setSMonthIdx}
                />
              </div>
              <div className="flex-none w-16">
                <WheelPicker
                  items={solarDays}
                  selectedIndex={sDayIdx}
                  onChange={setSDayIdx}
                />
              </div>
              <div className="flex-1">
                <WheelPicker
                  items={SHICHEN_LABELS}
                  selectedIndex={sShichenIdx}
                  onChange={setSShichenIdx}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex-1">
                <WheelPicker
                  items={LUNAR_YEARS}
                  selectedIndex={lYearIdx}
                  onChange={setLYearIdx}
                />
              </div>
              <div className="flex-none w-16">
                <WheelPicker
                  items={LUNAR_MONTHS}
                  selectedIndex={lMonthIdx}
                  onChange={setLMonthIdx}
                />
              </div>
              <div className="flex-none w-16">
                <WheelPicker
                  items={lunarDays}
                  selectedIndex={lDayIdx}
                  onChange={setLDayIdx}
                />
              </div>
              <div className="flex-1">
                <WheelPicker
                  items={SHICHEN_LABELS}
                  selectedIndex={lShichenIdx}
                  onChange={setLShichenIdx}
                />
              </div>
            </>
          )}
        </div>

        {/* 取消/完成 */}
        <div className="flex" style={{ borderTop: "1px solid #e8c87a" }}>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-4 text-base font-semibold cursor-pointer whitespace-nowrap"
            style={{ background: "transparent", border: "none", color: "#666", fontFamily: "'Noto Serif SC', serif" }}
          >
            取消
          </button>
          <div style={{ width: "1px", background: "#e8c87a" }} />
          <button
            type="button"
            onClick={handleConfirm}
            className="flex-1 py-4 text-base font-bold cursor-pointer whitespace-nowrap"
            style={{ background: "#c0000a", border: "none", color: "#fff", fontFamily: "'Noto Serif SC', serif" }}
          >
            完成
          </button>
        </div>
      </div>
    </div>
  );
}
